import { Fragment, useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver-es';

import { backendHOST } from '../../../../client-config/client-config';

import { logout } from '../../../../actions/auth';
import { withRouter } from '../../../hooks/withRouter';

const url = `${backendHOST}/api/scriptantiaAdmin`;

const FormDiakDijak = ({ auth }) => {
  const [rows, setRows] = useState([]);

  const [searchText, setSearchText] = useState('');
  const [viewMode, setViewMode] = useState('problematic');

  const [loading, setLoading] = useState(false);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    'x-auth-token': localStorage.token,
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const numberValue = (value) => {
    const number = Number(value || 0);

    return Number.isNaN(number) ? 0 : number;
  };

  const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const number = Number(value);

    if (Number.isNaN(number)) {
      return String(value);
    }

    return `${new Intl.NumberFormat('hu-HU', {
      maximumFractionDigits: 0,
    }).format(number)} Ft`;
  };

  const isPlannedLesson = (value) => {
    if (typeof value === 'boolean') return value;

    const normalized = String(value || '')
      .trim()
      .toLowerCase();

    return ['1', 'true', 'igen', 'yes', 'y'].includes(normalized);
  };

  const getDifference = (row) => {
    if (row.Difference !== null && row.Difference !== undefined) {
      return numberValue(row.Difference);
    }

    return numberValue(row.DiakDija) - numberValue(row.AlapDij);
  };

  const getIncrease = (row) => {
    if (row.Increase !== null && row.Increase !== undefined) {
      return numberValue(row.Increase);
    }

    return numberValue(row.AlapDij) - numberValue(row.DiakDija);
  };

  const isBelowListPrice = (row) => {
    return getDifference(row) < 0;
  };

  const shouldIncrease = (row) => {
    return getIncrease(row) > 0 || isBelowListPrice(row);
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${url}/DiakDijak`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error('Nem sikerült betölteni a diák óradíj riportot.');
      }

      const data = await response.json();

      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Ismeretlen hiba történt betöltés közben.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enrichedRows = useMemo(() => {
    return rows.map((row) => {
      const difference = getDifference(row);
      const increase = getIncrease(row);
      const plannedLesson = isPlannedLesson(row.OraTervben);

      return {
        ...row,
        _studentName: getNestedValue(row, 'diak.text') || '',
        _paymentFrequency: getNestedValue(row, 'diak.payment_frequency') || '',
        _subjectName: getNestedValue(row, 'tantargy.tantargy_descr') || '',
        _difference: difference,
        _increase: increase,
        _plannedLesson: plannedLesson,
        _belowListPrice: difference < 0,
        _shouldIncrease: increase > 0 || difference < 0,
      };
    });
  }, [rows]);

  const sortedRows = useMemo(() => {
    return [...enrichedRows].sort((a, b) => {
      if (a._shouldIncrease !== b._shouldIncrease) {
        return a._shouldIncrease ? -1 : 1;
      }

      if (a._plannedLesson !== b._plannedLesson) {
        return a._plannedLesson ? -1 : 1;
      }

      const diffCompare = a._difference - b._difference;

      if (diffCompare !== 0) {
        return diffCompare;
      }

      const studentCompare = String(a._studentName).localeCompare(
        String(b._studentName),
        'hu',
      );

      if (studentCompare !== 0) {
        return studentCompare;
      }

      return String(a._subjectName).localeCompare(String(b._subjectName), 'hu');
    });
  }, [enrichedRows]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    let visibleRows = sortedRows;

    if (viewMode === 'problematic') {
      visibleRows = visibleRows.filter((row) => row._shouldIncrease);
    }

    if (viewMode === 'plannedProblematic') {
      visibleRows = visibleRows.filter(
        (row) => row._plannedLesson && row._shouldIncrease,
      );
    }

    if (viewMode === 'planned') {
      visibleRows = visibleRows.filter((row) => row._plannedLesson);
    }

    if (!normalizedSearch) {
      return visibleRows;
    }

    return visibleRows.filter((row) =>
      [
        row.id_user,
        row._studentName,
        row._paymentFrequency,
        row._subjectName,
        row.DiakDija,
        row.DiakTavalyiDija,
        row.AlapDij,
        row.OraTervben,
        row._difference,
        row._increase,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch),
        ),
    );
  }, [sortedRows, searchText, viewMode]);

  const stats = useMemo(() => {
    const belowListPriceRows = enrichedRows.filter(
      (row) => row._belowListPrice,
    );
    const shouldIncreaseRows = enrichedRows.filter(
      (row) => row._shouldIncrease,
    );
    const plannedRows = enrichedRows.filter((row) => row._plannedLesson);
    const plannedProblematicRows = enrichedRows.filter(
      (row) => row._plannedLesson && row._shouldIncrease,
    );

    const totalMissing = belowListPriceRows.reduce(
      (sum, row) => sum + Math.abs(numberValue(row._difference)),
      0,
    );

    const totalSuggestedIncrease = shouldIncreaseRows.reduce(
      (sum, row) => sum + Math.max(0, numberValue(row._increase)),
      0,
    );

    return {
      totalRows: enrichedRows.length,
      belowListPriceCount: belowListPriceRows.length,
      shouldIncreaseCount: shouldIncreaseRows.length,
      plannedCount: plannedRows.length,
      plannedProblematicCount: plannedProblematicRows.length,
      totalMissing,
      totalSuggestedIncrease,
    };
  }, [enrichedRows]);

  const exportToExcel = async () => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Diák óradíjak');

    worksheet.columns = [
      { header: 'Diák azonosító', key: 'id_user', width: 14 },
      { header: 'Diák neve', key: 'studentName', width: 28 },
      { header: 'Fizetési mód', key: 'paymentFrequency', width: 16 },
      { header: 'Tantárgy', key: 'subjectName', width: 28 },
      { header: 'Aktuális óradíj', key: 'diakDija', width: 16 },
      { header: 'Tavalyi óradíj', key: 'diakTavalyiDija', width: 16 },
      { header: 'Listaár', key: 'alapDij', width: 16 },
      { header: 'Van betervezett óra', key: 'oraTervben', width: 18 },
      { header: 'Eltérés', key: 'difference', width: 16 },
      { header: 'Emelés', key: 'increase', width: 16 },
      { header: 'Állapot', key: 'status', width: 22 },
    ];

    filteredRows.forEach((row) => {
      worksheet.addRow({
        id_user: row.id_user,
        studentName: row._studentName,
        paymentFrequency: row._paymentFrequency,
        subjectName: row._subjectName,
        diakDija: numberValue(row.DiakDija),
        diakTavalyiDija: numberValue(row.DiakTavalyiDija),
        alapDij: numberValue(row.AlapDij),
        oraTervben: row._plannedLesson ? 'Igen' : 'Nem',
        difference: numberValue(row._difference),
        increase: numberValue(row._increase),
        status: row._shouldIncrease ? 'Emelés javasolt' : 'Rendben',
      });
    });

    worksheet.getRow(1).font = { bold: true };

    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'left',
        };
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], {
        type: 'application/octet-stream',
      }),
      'Diak-oradijak-attekinto.xlsx',
    );

    setMessage('Excel export elkészült.');
  };

  const KpiCard = ({
    title,
    value,
    subtitle,
    icon,
    color = 'primary.main',
  }) => {
    return (
      <Card variant='outlined' sx={{ borderRadius: 2, height: '100%' }}>
        <CardContent>
          <Stack spacing={1}>
            <Stack direction='row' justifyContent='space-between' spacing={1}>
              <Typography variant='body2' color='text.secondary'>
                {title}
              </Typography>

              <Box sx={{ color }}>{icon}</Box>
            </Stack>

            <Typography
              variant='h5'
              sx={{
                fontWeight: 800,
                color,
              }}
            >
              {value}
            </Typography>

            {subtitle && (
              <Typography variant='caption' color='text.secondary'>
                {subtitle}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const AmountCell = ({ value, mode = 'default' }) => {
    let color = 'text.primary';

    if (mode === 'difference') {
      const diff = numberValue(value);
      color =
        diff < 0 ? 'warning.main' : diff > 0 ? 'success.main' : 'text.primary';
    }

    if (mode === 'increase') {
      const inc = numberValue(value);
      color = inc > 0 ? 'warning.main' : 'success.main';
    }

    return (
      <TableCell align='right'>
        <Typography
          component='span'
          sx={{
            fontWeight: 700,
            color,
            whiteSpace: 'nowrap',
          }}
        >
          {formatMoney(value)}
        </Typography>
      </TableCell>
    );
  };

  return (
    <Fragment>
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  alignItems={{ xs: 'stretch', lg: 'center' }}
                  justifyContent='space-between'
                  spacing={2}
                >
                  <Box>
                    <Typography variant='h5' sx={{ fontWeight: 800 }}>
                      Diák óradíjak áttekintő
                    </Typography>

                    <Typography variant='body2' color='text.secondary'>
                      Aktuális diák óradíjak összevetése a listaárral és a
                      tavalyi díjakkal.
                    </Typography>
                  </Box>

                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                  >
                    <FormControl size='small' sx={{ minWidth: 230 }}>
                      <InputLabel id='diak-dijak-view-mode-label'>
                        Nézet
                      </InputLabel>
                      <Select
                        labelId='diak-dijak-view-mode-label'
                        label='Nézet'
                        value={viewMode}
                        onChange={(event) => setViewMode(event.target.value)}
                      >
                        <MenuItem value='problematic'>
                          Csak eltérő / emelendő
                        </MenuItem>
                        <MenuItem value='plannedProblematic'>
                          Csak betervezett és emelendő
                        </MenuItem>
                        <MenuItem value='planned'>
                          Csak betervezett órás
                        </MenuItem>
                        <MenuItem value='all'>Minden tétel</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      label='Keresés'
                      size='small'
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                      sx={{ minWidth: 260 }}
                    />

                    <Button
                      variant='outlined'
                      startIcon={<RefreshIcon />}
                      onClick={loadData}
                      disabled={loading}
                    >
                      Frissítés
                    </Button>

                    <Button
                      variant='outlined'
                      startIcon={<FileDownloadIcon />}
                      onClick={exportToExcel}
                      disabled={loading || filteredRows.length === 0}
                    >
                      Export Excel
                    </Button>
                  </Stack>
                </Stack>

                {error && (
                  <Alert severity='error' onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                <Alert severity='info'>
                  A lista alapból azokat a sorokat mutatja, ahol az aktuális
                  diákdíj eltér vagy elmarad a listaártól. A betervezett órás
                  eltérések a legsürgősebbek.
                </Alert>
              </Stack>
            </CardContent>
          </Card>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: 2,
            }}
          >
            <KpiCard
              title='Listaár alatti sorok'
              value={stats.belowListPriceCount}
              subtitle={`Összes eltérés: ${formatMoney(stats.totalMissing)}`}
              icon={<WarningAmberIcon />}
              color='warning.main'
            />

            <KpiCard
              title='Emelés javasolt'
              value={stats.shouldIncreaseCount}
              subtitle={`Javasolt emelés összesen: ${formatMoney(
                stats.totalSuggestedIncrease,
              )}`}
              icon={<TrendingUpIcon />}
              color='warning.main'
            />

            <KpiCard
              title='Betervezett órás tételek'
              value={stats.plannedCount}
              subtitle='Ezek aktív működésben érintettek'
              icon={<EventAvailableIcon />}
              color='primary.main'
            />

            <KpiCard
              title='Betervezett + emelendő'
              value={stats.plannedProblematicCount}
              subtitle='Ezeket érdemes elsőként rendezni'
              icon={<PriceChangeIcon />}
              color='error.main'
            />
          </Box>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <Chip label={`${filteredRows.length} sor`} variant='outlined' />
            <Chip
              label={`Összes adat: ${stats.totalRows} sor`}
              variant='outlined'
            />
            <Chip
              label={`Listaár alatti: ${stats.belowListPriceCount}`}
              color='warning'
              variant='outlined'
            />
            <Chip
              label={`Betervezett + emelendő: ${stats.plannedProblematicCount}`}
              color='error'
              variant='outlined'
            />
          </Stack>

          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  variant='outlined'
                  sx={{ maxHeight: '72vh', overflow: 'auto' }}
                >
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Diák azonosító</TableCell>
                        <TableCell>Diák neve</TableCell>
                        <TableCell>Fiz. mód</TableCell>
                        <TableCell>Tantárgy</TableCell>
                        <TableCell align='right'>Aktuális díj</TableCell>
                        <TableCell align='right'>Tavalyi díj</TableCell>
                        <TableCell align='right'>Listaár</TableCell>
                        <TableCell align='center'>Betervezett óra</TableCell>
                        <TableCell align='right'>Eltérés</TableCell>
                        <TableCell align='right'>Emelés</TableCell>
                        <TableCell>Állapot</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredRows.map((row, index) => {
                        const key = `${row.id_user}_${row._subjectName}_${index}`;

                        return (
                          <TableRow
                            key={key}
                            hover
                            sx={{
                              bgcolor:
                                row._plannedLesson && row._shouldIncrease
                                  ? 'rgba(244, 67, 54, 0.06)'
                                  : row._shouldIncrease
                                  ? 'rgba(255, 152, 0, 0.06)'
                                  : 'inherit',
                            }}
                          >
                            <TableCell>{row.id_user}</TableCell>

                            <TableCell sx={{ fontWeight: 700 }}>
                              {row._studentName}
                            </TableCell>

                            <TableCell>{row._paymentFrequency}</TableCell>

                            <TableCell>{row._subjectName}</TableCell>

                            <AmountCell value={row.DiakDija} />

                            <AmountCell value={row.DiakTavalyiDija} />

                            <AmountCell value={row.AlapDij} />

                            <TableCell align='center'>
                              <Chip
                                label={row._plannedLesson ? 'Igen' : 'Nem'}
                                size='small'
                                color={
                                  row._plannedLesson ? 'primary' : 'default'
                                }
                                variant='outlined'
                              />
                            </TableCell>

                            <AmountCell
                              value={row._difference}
                              mode='difference'
                            />

                            <AmountCell value={row._increase} mode='increase' />

                            <TableCell>
                              {row._plannedLesson && row._shouldIncrease ? (
                                <Chip
                                  label='Sürgős emelés'
                                  size='small'
                                  color='error'
                                  variant='outlined'
                                />
                              ) : row._shouldIncrease ? (
                                <Chip
                                  label='Emelés javasolt'
                                  size='small'
                                  color='warning'
                                  variant='outlined'
                                />
                              ) : (
                                <Chip
                                  label='Rendben'
                                  size='small'
                                  color='success'
                                  variant='outlined'
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={11} align='center'>
                            Nincs megjeleníthető diák óradíj adat.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        message={message}
      />
    </Fragment>
  );
};

FormDiakDijak.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { logout })(withRouter(FormDiakDijak));
